import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { EmployeeService } from './employee.service';
import { APPOINTMENT_REQUEST, LocalStorageService } from './local-storage.service';
import { ServiceService } from './service.service';

@Injectable({
    providedIn: 'root',
})
export class AppointmentRequestRefreshService {
    constructor(
        private localStorageService: LocalStorageService,
        private employeeService: EmployeeService,
        private serviceService: ServiceService,
        private router: Router
    ) {}

    /**
     * Refreshes the appointment request with current employee and service data to prevent stale prices and durations.
     * If the employee no longer exists or can't perform the selected services, the appointment request is cleared.
     * @param apptRequest - The appointment request to refresh, or null if none exists
     * @param redirectOnClear - Optional route to redirect to if appointment request is cleared (e.g., '/booking/service')
     * @param forceRefreshFirstAvailable - If true, refresh First Available selections that were already resolved to a specific employee
     */
    public async refreshAppointmentRequestIfNeeded(
        apptRequest: AppointmentRequest | null,
        redirectOnClear?: string,
        forceRefreshFirstAvailable?: boolean
    ): Promise<void> {
        // If there's no appointment request or it's incomplete, nothing to refresh
        if (!apptRequest || !apptRequest.employee?.length || !apptRequest.service?.length) {
            return;
        }

        const employee = apptRequest.employee[0];
        // If First Available is selected and we're not forcing a refresh, no need to refresh employee data
        if (employee?.firstAvailable && !forceRefreshFirstAvailable) {
            return;
        }

        // Get fresh employee and service data with current prices and durations
        const serviceIDList = apptRequest.service.map((s) => s.id);
        try {
            const [freshEmployeeList, freshServiceList] = await Promise.all([
                this.employeeService.getEmployeeList([employee.id], serviceIDList),
                this.serviceService.getServiceList(),
            ]);

            if (!freshEmployeeList?.length) {
                // Employee no longer exists or can't perform these services - clear appointment request
                this.localStorageService.remove(APPOINTMENT_REQUEST);
                if (redirectOnClear) {
                    const customerID = this.localStorageService.getString('customerID');
                    void this.router.navigate([redirectOnClear], {
                        queryParams: {
                            DSID: customerID,
                        },
                    });
                }
                return;
            }

            const freshEmployee = freshEmployeeList[0];

            // Check if employee can still perform all services
            const canPerformAllServices = apptRequest.service.every((service) =>
                freshEmployee.employeeServicePriceList?.some((esp: { serviceID: string }) => esp.serviceID === service.id)
            );

            if (!canPerformAllServices) {
                // Employee can no longer perform all services - clear appointment request
                this.localStorageService.remove(APPOINTMENT_REQUEST);
                if (redirectOnClear) {
                    const customerID = this.localStorageService.getString('customerID');
                    void this.router.navigate([redirectOnClear], {
                        queryParams: {
                            DSID: customerID,
                        },
                    });
                }
                return;
            }

            // Update the appointment request with fresh employee data (updated prices and duration exceptions)
            const updatedEmployee = {
                ...employee,
                employeeServicePriceList: freshEmployee.employeeServicePriceList,
                durationExceptionList: freshEmployee.durationExceptionList,
            };

            // Update services with fresh data from backend
            const updatedServices = apptRequest.service.map((existingService) => {
                const freshService = freshServiceList.find((s) => s.id === existingService.id);
                if (freshService) {
                    // Merge fresh service data while preserving appointment-specific properties
                    return { ...existingService, ...freshService };
                }
                return existingService;
            });

            // Update appointment request with both fresh employee and service data
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [updatedEmployee]);
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, updatedServices);
        } catch (error) {
            console.error('Failed to refresh appointment request:', error);
            // On error, clear the appointment request to be safe
            this.localStorageService.remove(APPOINTMENT_REQUEST);
            if (redirectOnClear) {
                const customerID = this.localStorageService.getString('customerID');
                void this.router.navigate([redirectOnClear], {
                    queryParams: {
                        DSID: customerID,
                    },
                });
            }
        }
    }
}
